import {
  Avatar,
  DatePicker,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Textarea,
  Spinner,
} from "@nextui-org/react";
import TaskMediaApi from "../../../services/taskMediaApi.js";
import { Gallery } from "react-grid-gallery";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import React, { useRef, useContext, useEffect, useState, useCallback, useMemo } from "react";
import closeIcon from "../../../Assets/popup/cross-square.svg";
import { AuthContext } from "Context/AuthContext";
import { Controller, useForm } from "react-hook-form";
import addTaskIcon from "../../../Assets/frond-details/tasklist/tasklist-icon.svg";
import userIcon from "../../../Assets/frond-details/tasklist/user.svg";
import axios from "axios";
import { toast } from "react-toastify";
import { PopUpContext } from "Context/PopUpContext";
import up from "../../../Assets/create-frond/up.svg";
import calendar from "../../../Assets/create-frond/calendar.svg";
import { parseDate } from "@internationalized/date";
import DeleteIcon from "../../../Assets/delete.png";


// Icons for media
const imageIcon = "🖼️";
const documentIcon = "📄";
const deleteIcon = "❌";
const uploadIcon = "📎";

const deleteButtonStyle = {
  position: 'absolute',
  top: '4px',
  right: '4px',
  zIndex: 9999, 
  pointerEvents: 'auto', 
};

// Media cache with TTL
const mediaCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const AddTask = ({ isOpen, onClose, frondId, isModalOpen, taskId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [selectedUser, setSelectedUser] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [addToTasks, setAddToTasks] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Media states
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [taskMedia, setTaskMedia] = useState([]);
  const [isMediaLoading, setIsMediaLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
const [mediaToDelete, setMediaToDelete] = useState(null);
  
  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  
  // Pagination
  const [imagePage, setImagePage] = useState(1);
  const [hasMoreImages, setHasMoreImages] = useState(false);
  const IMAGES_PER_PAGE = 12;

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      date: null,
      title: "",
      desc: "",
      status: "",
    },
  });

  const dateValue = watch("date");
  const statusValue = watch("status");
  const [descLength, setDescLength] = useState(0);
  const { token } = useContext(AuthContext);
  const { setIsAddTask, setIsAddEvent } = useContext(PopUpContext);

  // Handle resize for scroll
  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => {
        handleResize();
      }, 1000);
    }
  }, [isModalOpen, isOpen]);

  const handleResize = () => {
    const scrollOutter = document.querySelector(".scroll-outter");
    const scrollInner = document.querySelector(".scroll-inner");

    if (scrollOutter && scrollInner) {
      const scrollOutterWidth = scrollOutter.offsetWidth;
      const scrollInnerWidth = scrollInner.offsetWidth;
      scrollOutter.style.width = `calc(100% + ${
        scrollOutterWidth - scrollInnerWidth
      }px)`;
    }
  };

  // Load members on mount
  useEffect(() => {
    if (frondId) {
      getMembers();
    }
  }, [frondId]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && taskId) {
      loadTaskData();
    }
  }, [isOpen, taskId]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      resetStates();
    }
  }, [isOpen]);

  const resetStates = () => {
    setTaskMedia([]);
    setIsMediaLoading(false);
    setLightboxOpen(false);
    setPhotoIndex(0);
    setImagePage(1);
    reset();
    setSelectedUser(new Set([]));
    setSelectedImages([]);
    setSelectedDocuments([]);
    setAddToTasks(false);
    setUploadProgress(0);
  };

  const loadTaskData = async () => {
    setIsDataLoading(true);
    try {
      await Promise.all([
        getTaskData(),
        fetchTaskMedia(taskId)
      ]);
    } catch (error) {
      console.error('Error loading task data:', error);
    } finally {
      setIsDataLoading(false);
    }
  };

  const getTaskData = async () => {
    try {
      const { data } = await axios.get(
        `/tasks/${taskId}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const taskData = data?.response?.data;
      if (!taskData) return;

      const activeUsers = taskData.assigned_to?.filter(
        (user) => user.status === true
      ) || [];

      setSelectedUser(new Set(activeUsers.map((user) => user.name)));
      setValue("name", activeUsers.map((user) => user.name));

      if (taskData.task?.due_date) {
        const [year, month, day] = taskData.task.due_date.split("-");
        setValue("date", parseDate(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`));
      }

      setValue("desc", taskData.task?.description || "");
      setValue("title", taskData.task?.title || "");
      
      const statusMap = {
        pending: "Pending",
        in_progress: "In Progress",
        completed: "Completed"
      };
      setValue("status", statusMap[taskData.task?.status] || "");
      
      setDescLength(taskData.task?.description?.length || 0);
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to load task", {
        autoClose: 1500,
        position: "bottom-right",
        className: "rounded-4xl custom-toast error-toast",
      });
    }
  };

  const fetchTaskMedia = async (taskId) => {
    if (!taskId) return;
    
    // Check cache
    const cached = mediaCache.get(taskId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setTaskMedia(cached.data);
      setHasMoreImages(cached.data.length > IMAGES_PER_PAGE);
      return;
    }
    
    setIsMediaLoading(true);
    try {
      const media = await TaskMediaApi.getTaskMedia(token, taskId);
      
      // Store in cache
      mediaCache.set(taskId, {
        data: media,
        timestamp: Date.now()
      });
      
      setTaskMedia(media);
      setHasMoreImages(media.length > IMAGES_PER_PAGE);
    } catch (error) {
      console.error('Error fetching task media:', error);
      toast("Failed to load attachments", {
        autoClose: 1500,
        position: "bottom-right",
        className: "rounded-4xl custom-toast error-toast",
      });
    } finally {
      setIsMediaLoading(false);
    }
  };

  // Memoized computed values
  const images = useMemo(() => {
    return taskMedia
      .filter(m => m.type === 'image' || m.mimeType?.startsWith('image/'))
      .map((img) => ({
        src: img.thumbnail || img.url || img.original_url,
        thumbnail: img.thumbnail || (img.url ? `${img.url}?w=150&h=150&fit=thumb` : ''),
        width: img.width || 300,
        height: img.height || 200,
        originalSrc: img.url || img.original_url,
        id: img.id,
        name: img.name || img.filename,
      }));
  }, [taskMedia]);

  const documents = useMemo(() => {
    return taskMedia.filter(m => m.type === 'document');
  }, [taskMedia]);

  const paginatedImages = useMemo(() => {
    return images.slice(0, imagePage * IMAGES_PER_PAGE);
  }, [images, imagePage]);

  const getDocumentIcon = (mimeType) => {
    if (!mimeType) return '📄';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📊';
    if (mimeType.includes('text')) return '📄';
    return '📄';
  };

  const openLightbox = (index) => {
    setPhotoIndex(index);
    setLightboxOpen(true);
  };

  const handleDeleteMedia = async (mediaId) => {
   
    
    try {
      await TaskMediaApi.deleteMedia(token, taskId, mediaId);
      
      // Update cache
      const cached = mediaCache.get(taskId);
      if (cached) {
        const updatedMedia = cached.data.filter(m => m.id !== mediaId);
        mediaCache.set(taskId, {
          data: updatedMedia,
          timestamp: Date.now()
        });
        setTaskMedia(updatedMedia);
      }
      
      toast('Attachment deleted', {
        autoClose: 1500,
        position: "bottom-right",
        className: "rounded-4xl custom-toast success-toast",
      });
    } catch (error) {
      toast('Failed to delete attachment', {
        autoClose: 1500,
        position: "bottom-right",
        className: "rounded-4xl custom-toast error-toast",
      });
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(prev => [...prev, ...files]);
  };

  const handleDocumentSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedDocuments(prev => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeDocument = (index) => {
    setSelectedDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const loadMoreImages = () => {
    setImagePage(prev => prev + 1);
  };

  async function addTask(reqBody) {
    setIsLoading(true);
    setIsDisabled(true);

    const selectedUserNames = Array.from(selectedUser);
    const targetUserId = users
      .filter((user) => selectedUserNames.includes(user.name))
      .map((user) => user.id);

    const statusMap = {
      "Pending": "pending",
      "In Progress": "in_progress",
      "Completed": "completed"
    };
    const statusType = statusMap[reqBody.status] || "";

    const formData = new FormData();
    formData.append('title', reqBody.title);
    if (reqBody.desc) formData.append('description', reqBody.desc);
    formData.append('type', 'task');
    if (statusType) formData.append('status', statusType);
    
    if (reqBody.date) {
      const dueDate = `${reqBody.date.year}-${String(reqBody.date.month).padStart(2, '0')}-${String(reqBody.date.day).padStart(2, '0')}`;
      formData.append('due_date', dueDate);
    }

    targetUserId.forEach(id => formData.append('assigned_to[]', id));
    selectedImages.forEach(image => formData.append('images[]', image));
    selectedDocuments.forEach(doc => formData.append('documents[]', doc));

    try {
      const url = taskId 
        ? `/tasks/${taskId}`
        : `/projects/${frondId}/tasks`;
      
      if (taskId) formData.append('_method', 'PUT');
      
      await axios.post(url, formData, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      toast(taskId ? "Updates saved" : "Task created successfully", {
        autoClose: 1500,
        position: "bottom-right",
        className: "rounded-4xl custom-toast success-toast",
      });
      
      // Clear cache for this task
      mediaCache.delete(taskId);
      
      resetStates();
      onClose();
      
      setIsAddEvent(true);
      setTimeout(() => setIsAddEvent(false), 1000);
      setIsAddTask(true);
      setTimeout(() => setIsAddTask(false), 1000);

    } catch (error) {
      let errorMessage = "An error occurred";
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        errorMessage = Object.values(errors)[0]?.[0] || errorMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast(errorMessage, {
        autoClose: 1500,
        position: "bottom-right",
        className: "rounded-4xl custom-toast error-toast",
      });
    } finally {
      setIsDisabled(false);
      setIsLoading(false);
      setUploadProgress(0);
    }
  }


  // This replaces your previous handleDeleteMedia logic inside the button
const triggerDeleteConfirm = (e, mediaId) => {
  e.stopPropagation();
  e.preventDefault();
  setMediaToDelete(mediaId);
  setIsDeleteModalOpen(true);
};

const confirmDelete = async (e) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
  
  if (!mediaToDelete) return;

  try {
    // 1. Close the confirmation pop-up FIRST
    setIsDeleteModalOpen(false); 
    
    // 2. Perform the deletion logic
    await handleDeleteMedia(mediaToDelete);
    
    // 3. Clear the selection
    setMediaToDelete(null);
  } catch (err) {
    console.error(err);
    setIsDeleteModalOpen(false);
  }
};



  const getMembers = async () => {
    try {
      const { data } = await axios.get(
        `/projects/${frondId}/membersassign`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUsers(data);
    } catch (error) {
      toast(error.response?.data?.message || "Failed to load members", {
        autoClose: 1500,
        position: "bottom-right",
        className: "rounded-4xl custom-toast error-toast",
      });
    }
  };

  const statusList = ["Pending", "In Progress", "Completed"];

  useEffect(() => {
    handleResize();
  }, [descLength]);

  useEffect(() => {
    if (descLength === 0) {
      setTimeout(() => {
        handleResize();
      }, 1000);
    }
  }, [descLength]);

  // Loading skeleton for media
  const MediaSkeleton = () => (
    <div className="space-y-3">
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
        <div className="grid grid-cols-4 gap-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMediaGallery = () => {
    if (isMediaLoading) return <MediaSkeleton />;
    if (!taskMedia.length) return null;

    return (
      <div className="w-full space-y-4">
        {/* Images */}
        {paginatedImages.length > 0 && (
    <div>
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm text-cardGrayColor font-normal flex items-center gap-2">
          <img width="15" height="15" src="https://img.icons8.com/ios/20/578FCA/image--v1.png" alt="images" />
          <span>Images ({images.length})</span>
        </label>
      </div>
      
      <div className="relative z-30">
      <div className="relative z-30">
  <Gallery
    onClick={(index, image, event) => {
      if (event) {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        if (x > rect.width - 40 && y < 40) {
          return;
        }
      }
      openLightbox(index);
    }}
    images={paginatedImages.map(img => ({
      ...img,
      customOverlay: (
        <div 
          style={deleteButtonStyle}
          className="delete-container"
        >
          <button
            type="button"
            className=" rounded-full w-7 h-7 flex items-center justify-center text-white shadow-xl hover:scale-110 transition-transform"
            onMouseDown={(e) => {
              e.stopPropagation(); 
            }}
            onClick={async (e) => {
              e.stopPropagation(); 
              e.preventDefault();
            triggerDeleteConfirm(e,  img.id);
            }}
          >
            ❌
          </button>
        </div>
      )
    }))}
    rowHeight={80}
    margin={4}
  />
  {isDeleteModalOpen && (
  <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
  onClick={(e) => e.stopPropagation()}>
    <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl transform transition-all scale-100"
    onClick={(e) => e.stopPropagation()}>
      <h3 className="text-lg font-semibold text-gray-900">Delete Attachment?</h3>
      <p className="text-sm text-gray-500 mt-2">
        Are you sure you want to remove this file? This action cannot be undone.
      </p>
      
      <div className="mt-6 flex gap-3 justify-end">
        <button
          onClick={() =>
             setIsDeleteModalOpen(false)}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
        type="button"
          onClick={(e) => {
          e.stopPropagation(); 
          confirmDelete();
        }}
          className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors shadow-md"
        >
          Yes, Delete
        </button>
      </div>
    </div>
  </div>
)}
</div>
        {hasMoreImages && paginatedImages.length < images.length && (
          <button
            onClick={loadMoreImages}
            className="mt-3 text-sm text-mainBlue hover:underline"
          >
            Load more images...
          </button>
        )}
      </div>
    </div>
  )}
        
        {/* Documents */}
        {documents.length > 0 && (
          <div>
            <label className="flex items-center gap-2 mb-2 text-sm text-cardGrayColor font-normal">
              <img width="15" height="15" src="https://img.icons8.com/ultraviolet/20/EB4C4C/live-folder.png" alt="documents" />
              <span>Documents ({documents.length})</span>
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {documents.map((doc) => (
                <div 
                  key={doc.id}
                  className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-lg flex-shrink-0">{getDocumentIcon(doc.mimeType)}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-textColor truncate">
                        {doc.name || doc.filename || 'Document'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {doc.sizeFormatted || `${Math.round(doc.size / 1024)} KB`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => window.open(doc.url, '_blank')}
                      className="px-2 py-1 bg-mainBlue text-white text-xs rounded hover:bg-blue-600"
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      onClick={(e) => triggerDeleteConfirm(e,doc.id)}
                      className="p-1 text-red-500 hover:text-red-700"
                    >
                      <img width="16" height="16" src={DeleteIcon} alt="Delete" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {isDeleteModalOpen && (
  <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
  onClick={(e) => e.stopPropagation()}>
    <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl transform transition-all scale-100"
    onClick={(e) => e.stopPropagation()}>
      <h3 className="text-lg font-semibold text-gray-900">Delete Attachment?</h3>
      <p className="text-sm text-gray-500 mt-2">
        Are you sure you want to remove this file? This action cannot be undone.
      </p>
      
      <div className="mt-6 flex gap-3 justify-end">
        <button
          onClick={() =>
             setIsDeleteModalOpen(false)}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
        type="button"
          onClick={(e) => {
          e.stopPropagation(); 
          confirmDelete();
        }}
          className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors shadow-md"
        >
          Yes, Delete
        </button>
      </div>
    </div>
  </div>
)}
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={() => {
          onClose();
          setTimeout(resetStates, 300);
        }}
        placement="center"
        backdrop="opaque"
        classNames={{
          backdrop: "z-[9999]",
          wrapper: "z-[99999] overflow-hidden",
          footer: "justify-center items-center",
          body: "px-[30px] mb-2.5",
          closeButton: "hidden",
          base: "rounded-[20px] p-5",
        }}
      >
        <ModalContent>
          {(closeModal) => (
            <>
              <ModalHeader className="flex justify-between items-center p-0 pb-[15px] pt-[15px] px-5 mb-[15px] relative after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[0.5px] after:bg-grayBorder">
                <h3 className="text-lg text-textColor font-bold">
                  {taskId ? "Edit Task" : "Add New Task"}
                </h3>
                <button onClick={closeModal} aria-label="Close">
                  <img src={closeIcon} alt="Close" className="w-6 h-6" />
                </button>
              </ModalHeader>
              
              <form onSubmit={handleSubmit(addTask)}>
                <div className="max-h-[60vh] overflow-y-auto scroll-outter">
                  <ModalBody className="w-full scroll-inner">
                    <div className="relative about-edit-img sm:min-h-[295px] sm:max-h-[295px] min-h-[200px] max-h-[200px] sm:min-w-[100%] sm:max-w-[100%] min-w-[100%] max-w-[100%]">
                      <div className="content flex flex-col gap-5 items-center">
                        <Image
                          src={addTaskIcon}
                          className="w-[96px] h-[96px]"
                          alt="Add Task"
                        />

                        {/* Assigned Users Select */}
                        <Controller
                          name="name"
                          control={control}
                          rules={{}}
                          render={({ field }) => (
                            <Select
                              {...field}
                              isInvalid={errors.name?.message ? true : false}
                              errorMessage={errors.name?.message}
                              selectionMode="multiple"
                              selectedKeys={selectedUser}
                              onSelectionChange={(selected) => {
                                const updatedSet = new Set(selected.values());
                                setSelectedUser(updatedSet);
                              }}
                              selectorIcon={
                                <img
                                  src={up}
                                  alt="Arrow"
                                  className="w-[12px] h-[12px]"
                                />
                              }
                              items={users}
                              startContent={
                                [...selectedUser]?.length === 0 ? (
                                  <img
                                    src={userIcon}
                                    alt="User Icon"
                                    className="w-[18px] h-[18px]"
                                  />
                                ) : null
                              }
                              placeholder="Assigned to"
                              variant="bordered"
                              classNames={{
                                base: ` ${
                                  [...selectedUser].length > 1
                                    ? "min-h-[100px] h-[100px] max-h-[100px] overflow-hidden"
                                    : "min-h-[39px] h-[39px]"
                                }]`,
                                trigger: `rounded-[8px] px-4 border-[0.86px] border-solid flex-1 overflow-hidden ${
                                  errors.name?.message
                                    ? "invalid-text"
                                    : "border-[#E5E4EB]"
                                }`,
                                mainWrapper: "flex-1 max-h-full",
                                innerWrapper:
                                  "user-select-input overflow-y-auto min-w-[calc(100%+40px)]",
                                value: `text-cardGrayColor phone:text-base text-[10px] flex flex-wrap gap-2 flex-col ${
                                  [...selectedUser].length > 2 ? "pt-5" : ""
                                }`,
                              }}
                              renderValue={(items) => {
                                return items.map((item) => (
                                  <div
                                    key={item.key}
                                    className="flex items-center gap-2"
                                  >
                                    <Avatar
                                      alt={item.data.name}
                                      className="flex-shrink-0"
                                      size="sm"
                                      src={item.data.img_url}
                                    />
                                    <div className="flex flex-col">
                                      <span>{item.data.name}</span>
                                    </div>
                                  </div>
                                ));
                              }}
                            >
                              {(user) => (
                                <SelectItem
                                  key={user.name}
                                  textValue={user.name}
                                >
                                  <div className="flex gap-2 items-center">
                                    <Avatar
                                      alt={user.name}
                                      className="flex-shrink-0"
                                      size="sm"
                                      src={user.img_url}
                                    />
                                    <div className="flex flex-col">
                                      <span className="text-small">
                                        {user.name}
                                      </span>
                                    </div>
                                  </div>
                                </SelectItem>
                              )}
                            </Select>
                          )}
                        />

                        {/* Due Date Picker */}
                        <div className="date-wrapper relative w-full">
                          <div
                            className={`absolute left-[45px] ${
                              errors.date?.message
                                ? "top-0 translate-y-[33%]"
                                : "top-[50%] translate-y-[-50%]"
                            } text-cardGrayColor phone:text-base text-[10px] font-normal ${
                              dateValue ? "hidden" : "block"
                            }`}
                          >
                            Due Date
                          </div>
                          <Controller
                            name="date"
                            control={control}
                            rules={{}}
                            render={({ field }) => (
                              <DatePicker
                                {...field}
                                variant="bordered"
                                selectorIcon={
                                  <Image
                                    src={calendar}
                                    className="w-[18px] h-[18px]"
                                  />
                                }
                                isInvalid={!!errors.date?.message}
                                errorMessage={errors.date?.message}
                                classNames={{
                                  selectorButton:
                                    "order-first min-w-[18px] max-h-[18px] h-[18px] me-0",
                                }}
                                dateInputClassNames={{
                                  base: `mt-0 w-full ${
                                    errors.date?.message
                                      ? "h-[68px]"
                                      : "h-[39px]"
                                  }`,
                                  inputWrapper: `rounded-[8px] border-[0.86px] p-0 border-solid ${
                                    errors.date?.message
                                      ? "invalid-text"
                                      : "border-[#E5E4EB]"
                                  }`,
                                  innerWrapper:
                                    "px-[13px] h-full gap-x-[2px] no-action",
                                  segment: `date-picker-frond phone:text-base text-[10px] font-normal ${
                                    dateValue ? "block" : "hidden"
                                  }`,
                                }}
                              />
                            )}
                          />
                        </div>

                        {/* Task Title Input */}
                        <Controller
                          name="title"
                          control={control}
                          rules={{
                            required: "Title is required",
                            minLength: {
                              value: 3,
                              message: "Minimum number of characters is 3",
                            },
                            maxLength: {
                              value: 100,
                              message: "Maximum number of characters is 100",
                            },
                            validate: {
                              noTrailingSpace: (value) =>
                                !/\s$/.test(value) ||
                                "Title shouldn't end with a space",
                            },
                            pattern: {
                              value:
                                /^(?! )[a-zA-Z0-9\-\_\'\"\.\,\!\@\?\#\$\%\^\&\*\(\)\+\=\/\\\:]+(?!.*\s{2})[a-zA-Z0-9\-\/\\\_\'\"\:\.\,\!\@\#\$\%\^\&\*\(\)\+\= ]*$/gi,
                              message:
                                "Title may contain characters, numbers and special characters",
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              onKeyDown={(e) => {
                                e.key === "Enter" && e.preventDefault();
                              }}
                              {...field}
                              type="text"
                              variant="bordered"
                              placeholder="*Task Title"
                              isInvalid={errors.title?.message ? true : false}
                              errorMessage={errors.title?.message}
                              classNames={{
                                base: `input-noMargin ${
                                  errors.title?.message
                                    ? "h-[68px]"
                                    : "h-[39px]"
                                }`,
                                label:
                                  "text-textColor font-normal phone:text-sm text-[10px]",
                                input:
                                  "border-none px-[18px] phone:text-base text-[10px]",
                                mainWrapper: `${
                                  errors.title?.message
                                    ? "h-[68px]"
                                    : "h-[39px]"
                                }`,
                                innerWrapper: "h-[39px]",
                                inputWrapper: `border-[0.86px] p-0 border-solid ${
                                  errors.title?.message
                                    ? "invalid-text"
                                    : "border-[#E5E4EB]"
                                } rounded-[8px] h-[39px]`,
                              }}
                            />
                          )}
                        />

                        {/* Media Upload Section - Images */}
                        <div className="w-full">
                          <div className="flex items-center gap-2 mb-2">
                            <span>
                              <img width="15" height="15" src="https://img.icons8.com/ios/20/578FCA/image--v1.png" alt="image--v1"/> 
                            </span>
                            <span className="text-sm font-medium text-textColor">Images</span>
                          </div>
                          
                          {/* Image previews */}
                          {selectedImages.length > 0 && (
                            <div className="grid grid-cols-3 gap-2 mb-3">
                              {selectedImages.map((image, index) => (
                                <div key={index} className="relative group">
                                  <img
                                    src={URL.createObjectURL(image)}
                                    alt={`Preview ${index}`}
                                    className="w-full h-16 object-cover rounded-lg border border-gray-200"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="absolute -top-1 -right-1  text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                                  >
                                    {deleteIcon}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="relative mb-3">
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={handleImageSelect}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              id="image-upload"
                            />
                            <label
                              htmlFor="image-upload"
                              className="flex items-center justify-center gap-2 bg-gray-100 text-gray-600 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors text-sm border border-dashed border-gray-300"
                            >
                              <span>{uploadIcon}</span>
                              <span>Select Images</span>
                            </label>
                          </div>
                        </div>

                        {/* Media Upload Section - Documents */}
                        <div className="w-full">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">
                              <img width="15" height="15" src="https://img.icons8.com/ultraviolet/20/EB4C4C/live-folder.png" alt="live-folder"/>
                            </span>
                            <span className="text-sm font-medium text-textColor">Documents</span>
                          </div>
                          
                          {/* Document list */}
                          {selectedDocuments.length > 0 && (
                            <div className="space-y-2 mb-3">
                              {selectedDocuments.map((doc, index) => (
                                <div 
                                  key={index} 
                                  className="flex items-center justify-between bg-gray-50 p-2 rounded-lg border border-gray-200"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <span className="text-lg">{documentIcon}</span>
                                    <div className="flex-1">
                                      <p className="text-xs font-medium text-textColor truncate max-w-[150px]">
                                        {doc.name}
                                      </p>
                                      <p className="text-[10px] text-gray-500">
                                        {(doc.size / 1024).toFixed(1)} KB
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeDocument(index)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                  >
                                    {deleteIcon}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="relative mb-3">
                            <input
                              type="file"
                              multiple
                              onChange={handleDocumentSelect}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              id="document-upload"
                            />
                            <label
                              htmlFor="document-upload"
                              className="flex items-center justify-center gap-2 bg-gray-100 text-gray-600 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors text-sm border border-dashed border-gray-300"
                            >
                              <span>{uploadIcon}</span>
                              <span>Select Documents</span>
                            </label>
                          </div>
                        </div>

                        {/* Upload Progress Bar */}
                        {uploadProgress > 0 && uploadProgress < 100 && (
                          <div className="w-full">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-mainBlue h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 text-center">
                              Uploading: {uploadProgress}%
                            </p>
                          </div>
                        )}

                        {/* Existing Attachments (Edit Mode) */}
                        {taskId && (
                          <div className="w-full">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-lg">📎</span>
                              <span className="text-sm font-medium text-textColor">Existing Attachments</span>
                              {isMediaLoading && <Spinner size="sm" />}
                            </div>
                            
                            {renderMediaGallery()}
                            
                            {!isMediaLoading && taskMedia.length === 0 && (
                              <p className="text-sm text-cardGrayColor text-center py-2">No attachments</p>
                            )}
                          </div>
                        )}

                        {/* Status Select */}
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <Select
                              className="w-[100%]"
                              {...field}
                              variant="bordered"
                              labelPlacement="outside"
                              selectedKeys={field.value ? [field.value] : []}
                              placeholder="Select Status"
                              classNames={{
                                base: `input-noMargin ${
                                  errors.status?.message
                                    ? "h-[68px]"
                                    : "h-[39px]"
                                }`,
                                label: "text-textColor font-normal text-sm",
                                mainWrapper: "h-[42px]",
                                innerWrapper: "h-[42px] px-[18px]",
                                trigger: `p-0 h-[42px] border-[0.86px] border-solid ${
                                  errors.status?.message
                                    ? "invalid-text"
                                    : "border-[#E5E4EB]"
                                } rounded-[8px] flex items-center justify-between`,
                                value: `${
                                  statusValue
                                    ? "text-textColor"
                                    : "text-cardGrayColor"
                                } phone:text-base text-[10px] font-normal`,
                                selectorIcon: "w-[18px] h-[18px]",
                              }}
                              selectorIcon={<img src={up} />}
                              isInvalid={errors.status?.message ? true : false}
                              errorMessage={errors.status?.message}
                            >
                              {statusList.map((item) => (
                                <SelectItem key={item}>{item}</SelectItem>
                              ))}
                            </Select>
                          )}
                        />

                        {/* Description Textarea */}
                        <div className={`w-full mb-5`}>
                          <Controller
                            name="desc"
                            control={control}
                            rules={{
                              minLength: {
                                value: 2,
                                message: "Minimum number of characters is 2",
                              },
                              maxLength: {
                                value: 255,
                                message: "Maximum number of characters is 255",
                              },
                            }}
                            render={({ field }) => (
                              <Textarea
                                {...field}
                                onInput={(e) =>
                                  setDescLength(e.target.value.length)
                                }
                                maxLength={255}
                                type="text"
                                variant="bordered"
                                placeholder="*Add a task description..."
                                classNames={{
                                  base: "input-noMargin",
                                  label:
                                    "text-textColor font-normal phone:text-sm text-[10px]",
                                  input:
                                    "border-none px-[18px] phone:text-base text-[10px]",
                                  mainWrapper: "",
                                  innerWrapper: " overflow-y-auto",
                                  inputWrapper: `border-[0.86px] p-0 border-solid ${
                                    errors.desc?.message
                                      ? "invalid-text"
                                      : "border-[#E5E4EB]"
                                  } rounded-[8px] h-[102px] overflow-y-auto`,
                                }}
                                isInvalid={errors.desc?.message ? true : false}
                                errorMessage={errors.desc?.message}
                              />
                            )}
                          />
                          {!errors.desc?.message && 255 - descLength > 0 ? (
                            <p className="text-[.75rem] text-mainBlue mt-1">
                              {255 - descLength} remaining characters
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </ModalBody>
                </div>
                
                <ModalFooter className="shadow-modalShadow">
                  <button
                    disabled={isDisabled}
                    type="submit"
                    className="text-base font-bold text-white bg-mainGradiant rounded-[27px] py-[11px] px-[25px] w-[88px] min-h-[39px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <Spinner size="sm" color="white" />
                    ) : (
                      "Save"
                    )}
                  </button>
                </ModalFooter>
              </form>
            </>
          )}
        </ModalContent>
      </Modal>
      
      
      {lightboxOpen && (
        <Lightbox
          mainSrc={images[photoIndex]?.originalSrc}
          nextSrc={images[(photoIndex + 1) % images.length]?.originalSrc}
          prevSrc={images[(photoIndex + images.length - 1) % images.length]?.originalSrc}
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() => setPhotoIndex((photoIndex + images.length - 1) % images.length)}
          onMoveNextRequest={() => setPhotoIndex((photoIndex + 1) % images.length)}
          imageTitle={images[photoIndex]?.name}
          reactModalStyle={{ zIndex: 100001 }}
        />
      )}
    </>
  );
};

export default AddTask;